import { ExternalLink } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAuth } from "@/hooks/use-auth";
import { EmployeeAvatar } from "./EmployeeAvatar";
import { formatDepartment, type OrgEmployee } from "./types";

interface EmployeeSlideOverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee: OrgEmployee | null;
  manager: OrgEmployee | null;
  directReports: OrgEmployee[];
  totalSubtreeSize: number;
  onSelect: (employee: OrgEmployee) => void;
}

export function EmployeeSlideOver({
  open,
  onOpenChange,
  employee,
  manager,
  directReports,
  totalSubtreeSize,
  onSelect,
}: EmployeeSlideOverProps) {
  const { hasPermission, isManager } = useAuth();
  const canViewProfile = hasPermission("org_chart") || isManager;
  const isLeader = directReports.length > 0;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        {employee && (
          <>
            <SheetHeader>
              <div className="flex items-center gap-4 pt-2">
                <EmployeeAvatar
                  fullName={employee.fullName}
                  photoUrl={employee.slackPhotoUrl}
                  size={64}
                />
                <div className="min-w-0">
                  <SheetTitle className="text-left">{employee.fullName}</SheetTitle>
                  <SheetDescription className="text-left">
                    {employee.jobTitle ?? "Sem cargo definido"}
                    {employee.department ? ` · ${formatDepartment(employee.department)}` : ""}
                  </SheetDescription>
                </div>
              </div>
            </SheetHeader>

            <div className="mt-8 space-y-8">
              {isLeader && (
                <div className="grid grid-cols-2 gap-3 rounded-lg border bg-muted/30 p-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Reportes diretos
                    </div>
                    <div className="text-2xl font-semibold tabular-nums">
                      {directReports.length}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Equipe total
                    </div>
                    <div className="text-2xl font-semibold tabular-nums">{totalSubtreeSize}</div>
                  </div>
                </div>
              )}

              <Section title="Gestor direto">
                {manager ? (
                  <button
                    type="button"
                    onClick={() => onSelect(manager)}
                    className="w-full flex items-center gap-3 rounded-md p-2 hover:bg-accent text-left"
                  >
                    <EmployeeAvatar
                      fullName={manager.fullName}
                      photoUrl={manager.slackPhotoUrl}
                      size={32}
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{manager.fullName}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {manager.jobTitle ?? "—"}
                      </div>
                    </div>
                  </button>
                ) : (
                  <p className="text-sm text-muted-foreground">Sem gestor direto.</p>
                )}
              </Section>

              <Section title={`Reportes diretos (${directReports.length})`}>
                {directReports.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sem reportes diretos.</p>
                ) : (
                  <ul className="space-y-1">
                    {directReports.map((r) => (
                      <li key={r.id}>
                        <button
                          type="button"
                          onClick={() => onSelect(r)}
                          className="w-full flex items-center gap-3 rounded-md p-2 hover:bg-accent text-left"
                        >
                          <EmployeeAvatar
                            fullName={r.fullName}
                            photoUrl={r.slackPhotoUrl}
                            size={32}
                          />
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{r.fullName}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {r.jobTitle ?? "—"}
                            </div>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {canViewProfile && (
                <div className="pt-4 border-t">
                  <Link href={`/employees/${employee.id}`}>
                    <Button variant="outline" className="w-full gap-2">
                      Ver perfil completo
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
        {title}
      </h3>
      {children}
    </div>
  );
}
